import React, { useState, useMemo, useEffect } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

// Utils
import { pushDetalizationAnalytics } from 'utils/analytics/detalization';

// Components
import { ANALYTICS_DETAIL } from 'constants/detalization';
import { Text, Tabs, Dropdown } from '@beef/ui-kit';
import { DataBlocks, NewTable, Icon } from 'components';
import Heading from '../Heading';
import {
  fetchExpenses,
  toggleRoaming,
  filterPathCtn,
  setTableFilters,
} from '../../store/expensesData/action';
import { fetchExpensesPrepaid } from '../../store/expensesPrepaidData/action';
import { fetchPayments } from '../../store/paymentsData/action';
import { SectionWrapper } from '../SectionWrapper';
import UpdateByDate from '../UpdateByDate';

import { TABLE_TYPES, TABLE_CONFIGS } from './TableConfig';

// Styles
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const mapObject = (object, callback) => Object.fromEntries(Object.entries(object).map((([key, entry]) => [key, callback(key, entry)])));

const ExpensePage = ({ title }) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const [dropdowns, setDropdowns] = useState({
    balance: { selected: null, values: [] },
    call: { selected: null, values: [] },
    sms: { selected: null, values: [] },
  });
  const mapDropdowns = (callback) => setDropdowns(mapObject(dropdowns, callback));

  const [aggregateTypeList, setAggregateTypeList] = useState([]);

  const dispatch = useDispatch();
  const history = useHistory();

  const {
    structure,
    ctn,
    roaming,
    expensesPrepaid,
    isExpensesPrepaidFetching,
    isExpensesFetching,
    aggregateType,
    tableFilters,
  } = useSelector(({ detalizationNew }) => ({
    ctn: detalizationNew.expenses.ctn,
    structure: detalizationNew.expenses.structure,
    roaming: detalizationNew.expenses.roaming,
    expensesPrepaid: detalizationNew.expensesPrepaid,
    isExpensesPrepaidFetching: detalizationNew.expensesPrepaid.isFetching,
    isExpensesFetching: detalizationNew.expenses.isFetching,
    aggregateType: detalizationNew.expenses.aggregateType,
    tableFilters: detalizationNew.expenses.tableFilters,
  }), shallowEqual);

  useEffect(() => {
    dispatch(fetchExpenses());
    dispatch(fetchPayments());
    Object.keys(tableFilters).forEach((filterName) => dispatch(setTableFilters(filterName, 'empty')));

    if (history.location.pathname.startsWith('/detalization/roaming/')) {
      dispatch(toggleRoaming(true));
    }

    pushDetalizationAnalytics(ANALYTICS_DETAIL.SPEND_TYPE_CLICK);
  }, []);

  useEffect(() => {
    if (!expensesPrepaid.structure?.dropdown?.balance) return;

    mapDropdowns((dKey, dEntry) => {
      if (!(dKey in expensesPrepaid.structure?.dropdown)) throw new Error(`Missing key "${dKey}" in expensesPrepaid.structure!`);
      const values = Object.entries(expensesPrepaid.structure?.dropdown?.[dKey]).map(([key, value]) => ({ slug: key, label: value }));
      const selected = dEntry.selected ?? values[0]?.slug;
      return { selected, values };
    });
  }, [expensesPrepaid]);

  const onHistoryEvent = () => {
    if (history.location.pathname.startsWith('/detalization/roaming/')) {
      dispatch(toggleRoaming(true));
    }
    dispatch(filterPathCtn(history.location.pathname));
    mapDropdowns((dKey, dEntry) => ({ ...dEntry, selected: null }));
  };

  useEffect(() => history.listen(onHistoryEvent), []);
  useEffect(onHistoryEvent, [aggregateType]);

  const tabs = ctn?.[roaming ? 'ctnRoaming' : 'ctnNoRoaming']?.map((item) => ({
    id: item.id,
    title: item.title,
    href: item.href,
  }));

  const FiltersType = { Balance: 'Balance', Number: 'Number', Sms: 'Sms' };

  const handleAnyFilter = (filterType, setterKey, filter) => (value) => {
    setDropdowns({ ...dropdowns, [setterKey]: { ...dropdowns[setterKey], selected: value } });
    const action = value === 'empty' ? 'empty' : (x) => filter(x, value);
    dispatch(setTableFilters(filterType, action));
  };

  const XFilters = {
    [FiltersType.Balance]: {
      dropdown: 'balance',
      filter: (x, value) => x?.balances?.[0]?.nameCode === value,
      isAggregate: () => true,
    },
    [FiltersType.Number]: {
      dropdown: 'call',
      filter: (x, value) => x?.filterId === value,
      isAggregate: () => aggregateType === ('LOCAL_OUTCOMING_CALLS' || 'INTERNATIONAL_ROAMING_OUTCOMING_CALLS'),
    },
    [FiltersType.Sms]: {
      dropdown: 'sms',
      filter: (x, value) => x?.filterId === value,
      isAggregate: () => aggregateType === ('LOCAL_OUTCOMING_SMS_MMS' || 'INTERNATIONAL_ROAMING_SMS_MMS'),
    },
  };
  const calcDropdownLength = (key, minLength = 0) => (expensesPrepaid?.structure?.dropdown?.[key]
    ? (Object.keys(expensesPrepaid?.structure?.dropdown?.[key]).length > minLength) : false);

  useEffect(() => {
    const initSelectTab = ctn?.[roaming ? 'ctnRoaming' : 'ctnNoRoaming']?.filter((item) => item.href === history.location.pathname);

    if (initSelectTab) {
      setSelectedTab(initSelectTab[0]?.id);
    }
  }, [ctn, selectedTab, expensesPrepaid]);

  useEffect(() => () => {
    Object.keys(XFilters).forEach((filterName) => dispatch(setTableFilters(filterName, 'empty')));
  }, []);

  useEffect(() => {
    Object.keys(XFilters).forEach((filterName) => dispatch(setTableFilters(filterName, 'empty')));

    if (aggregateType === undefined || aggregateType === null) {
      return;
    }
    switch (aggregateType) {
      case 'LOCAL_INTERNET_ND':
      case 'INTERNATIONAL_ROAMING_INTERNET':
        setAggregateTypeList([aggregateType]);
        dispatch(fetchExpensesPrepaid([aggregateType]));
        break;
      case 'LOCAL_OUTCOMING_CALLS':
        setAggregateTypeList(['INTERNATIONAL_CALLS_ND', 'LOCAL_OUTCOMING_CALLS_RUSSIA', 'LOCAL_INCOMING_CALLS']);
        dispatch(fetchExpensesPrepaid(['INTERNATIONAL_CALLS_ND', 'LOCAL_OUTCOMING_CALLS_RUSSIA', 'LOCAL_INCOMING_CALLS']));
        break;
      case 'LOCAL_OUTCOMING_SMS_MMS':
        setAggregateTypeList(['INTERNATIONAL_SMS_MMS_ND', 'SMS_MMS_RUSSIA', 'LOCAL_INCOMING_SMS_MMS']);
        dispatch(fetchExpensesPrepaid(['INTERNATIONAL_SMS_MMS_ND', 'SMS_MMS_RUSSIA', 'LOCAL_INCOMING_SMS_MMS']));
        break;
      case 'INTERNATIONAL_ROAMING_OUTCOMING_CALLS':
        setAggregateTypeList(['INTERNATIONAL_ROAMING_INCOMING_CALLS', aggregateType]);
        dispatch(fetchExpensesPrepaid(['INTERNATIONAL_ROAMING_INCOMING_CALLS', aggregateType]));
        break;
      case 'INTERNATIONAL_ROAMING_SMS_MMS':
        setAggregateTypeList(['INTERNATIONAL_ROAMING_INCOMING_SMS_MMS', aggregateType]);
        dispatch(fetchExpensesPrepaid(['INTERNATIONAL_ROAMING_INCOMING_SMS_MMS', aggregateType]));
        break;
      default:
        throw new Error(`Unknown aggregateType: ${aggregateType}`);
    }
  }, [aggregateType]);

  const handleChangeTab = (value) => {
    const link = tabs.filter((item) => item.id === value);

    setSelectedTab(value);
    history.replace(link[0].href);
  };

  const isAnyFetching = isExpensesFetching || isExpensesPrepaidFetching;

  const dataTable = useMemo(() => {
    const textError = 'За этот период нет списаний. Выберите другой период.';

    const defReturn = { tableBody: [], mobileColumns: [], desktopColumns: [], dateGetter: undefined, textError };
    const serverError = expensesPrepaid.error;

    if (isAnyFetching) return defReturn;
    if (!aggregateType) return defReturn;

    let tableBody = null;
    let tableType = null;

    switch (aggregateType) {
      case 'LOCAL_OUTCOMING_SMS_MMS':
      case 'INTERNATIONAL_ROAMING_SMS_MMS':
        tableBody = expensesPrepaid.structure.data;
        tableType = TABLE_TYPES.SMS;
        break;
      case 'LOCAL_INTERNET_ND':
      case 'INTERNATIONAL_ROAMING_INTERNET':
        tableBody = expensesPrepaid.structure.data;
        tableType = TABLE_TYPES.INTERNET;
        break;
      case 'LOCAL_OUTCOMING_CALLS':
      case 'INTERNATIONAL_ROAMING_OUTCOMING_CALLS':
        tableBody = expensesPrepaid.structure.data;
        tableType = TABLE_TYPES.CALL;
        break;
      default:
        throw new Error(`Unknown selected tab: ${selectedTab}`);
    }

    const tableConfig = TABLE_CONFIGS[tableType];
    return { ...tableConfig, tableBody, textError, serverError };
  }, [isExpensesFetching, structure, expensesPrepaid, selectedTab]);

  return (
    <DataBlocks.Container>
      <DataBlocks.Block>
        <NavLink
          to="/detalization"
          className={cx('back-link')}
        >
          <Icon
            name="newarrow"
          />
          <Text size="size6-r">Назад</Text>
        </NavLink>
        <Heading title={title} className={cx('title')}>
          <SectionWrapper
            isFetching={isExpensesFetching}
            skeletonName="expensePageTabs"
          >
            {tabs?.length > 0 && (
              <Tabs
                onChange={(val) => handleChangeTab(val)}
                selectedTab={selectedTab}
                tabs={tabs}
                variant="secondary"
                isScrollable
              />
            )}
          </SectionWrapper>
        </Heading>

        <div className={cx('calendar')}>
          <UpdateByDate aggregateType={aggregateTypeList} />
        </div>

        <div className={cx('dropdown-wrapper')}>
          {Object.entries(XFilters).filter(([, entry]) => (calcDropdownLength(entry.dropdown, 2) && entry.isAggregate())).map(([filterName, entry]) => (
            <Dropdown
              options={dropdowns[entry.dropdown].values}
              value={dropdowns[entry.dropdown].selected}
              onChange={handleAnyFilter(filterName, entry.dropdown, entry.filter)}
            />
          ))}
        </div>
        <div className={cx('skeleton-wrapper')}>
          <SectionWrapper
            isFetching={isExpensesPrepaidFetching}
            skeletonName="table"
          >
            <NewTable {...dataTable} />
          </SectionWrapper>
        </div>
      </DataBlocks.Block>
    </DataBlocks.Container>
  );
};

ExpensePage.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  date: PropTypes.string,
  tabs: PropTypes.array,
  dropdown: PropTypes.array,
};
export default ExpensePage;
